import '../../utils/webgpuPolyfill.js'

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'

const MIN_DPR = 1.0
const MAX_DPR = 1.0
const getClampedDPR = () => Math.max(MIN_DPR, Math.min(window.devicePixelRatio || 1, MAX_DPR))
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { useIframeStore } from '../../store/iframeStore'
import WebGPU from 'three/examples/jsm/capabilities/WebGPU.js'
import * as THREE from 'three/webgpu'
import { WebGLRenderer, PCFSoftShadowMap } from 'three'
import PreloadManager from '../PreloadManager'
import { SceneReadyProvider } from '../SceneReadyManager'

// Import WebGPURenderer from the THREE.webgpu namespace
const { WebGPURenderer } = THREE

// Component to display renderer info in top right (hidden)
function RendererInfo({ rendererType }) {
  return null
}

// Pauses the render loop while an iframe 3D scene is active OR while the app
// is idle (no interaction for IDLE_TIMEOUT_MS on tablet portrait), then waits
// for 2 rendered frames before un-hiding markers so Drei <Html> transforms
// are current before the markers become visible again.
const IDLE_TIMEOUT_MS = 10_000

function FrameLoopController() {
  const showIframe    = useIframeStore(state => state.showIframe)
  const isIdlePaused  = useIframeStore(state => state.isIdlePaused)
  const setMarkersVisible = useIframeStore(state => state.setMarkersVisible)
  const { set, get } = useThree()
  const resumingRef   = useRef(false)
  const frameCountRef = useRef(0)

  // Derived: should the loop be paused for any reason?
  const shouldPause = showIframe || isIdlePaused

  useLayoutEffect(() => {
    if (shouldPause) {
      setMarkersVisible(false)
      set({ frameloop: 'demand' })
    } else {
      // Reset the R3F clock so the first resumed frame doesn't get a huge delta
      // (accumulated during the demand pause) which would cause Earth to jump rotation.
      get().clock.getDelta()
      // Resume loop — markers stay hidden until useFrame confirms 2 frames rendered
      resumingRef.current = true
      frameCountRef.current = 0
      set({ frameloop: 'always' })
    }
  }, [shouldPause, set, setMarkersVisible, get])

  useFrame(() => {
    if (resumingRef.current) {
      frameCountRef.current += 1
      if (frameCountRef.current >= 2) {
        resumingRef.current = false
        setMarkersVisible(true)
      }
    }
  })

  return null
}

// Watches for user interaction and pauses the render loop after IDLE_TIMEOUT_MS
// of inactivity. Only active when mediaQueryKey === 'tabletPortrait'.
function IdleDetector({ mediaQueryKey }) {
  const setIdlePaused = useIframeStore(state => state.setIdlePaused)
  const isActive = mediaQueryKey === 'tabletPortrait' || mediaQueryKey === 'tabletLandscape'
  const timerRef = useRef(null)

  useEffect(() => {
    if (!isActive) {
      // Leaving tablet portrait — cancel any pending timer and ensure loop is running
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      setIdlePaused(false)
      return
    }

    const resetTimer = () => {
      // If we were idle-paused, resume before restarting the countdown
      setIdlePaused(false)

      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        setIdlePaused(true)
      }, IDLE_TIMEOUT_MS)
    }

    const EVENTS = ['mousemove', 'mousedown', 'touchstart', 'touchmove', 'keydown', 'scroll']
    EVENTS.forEach(e => window.addEventListener(e, resetTimer, { passive: true }))

    // Start the initial countdown as soon as tablet portrait is detected
    resetTimer()

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, resetTimer))
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isActive, setIdlePaused])

  return null
}

// Component to handle WebGPU renderer resize
function WebGPUResizeHandler() {
  const { gl, size } = useThree()
  const lastSizeRef = React.useRef({ width: 0, height: 0 })
  
  useEffect(() => {
    // CRITICAL FIX: Only resize if dimensions actually changed
    // Prevents unnecessary buffer recreation in WebGPU backend
    if (gl && gl.setSize) {
      const hasChanged = 
        lastSizeRef.current.width !== size.width || 
        lastSizeRef.current.height !== size.height
      
      if (hasChanged) {
        gl.setSize(size.width, size.height, false)
        gl.setPixelRatio(getClampedDPR())
        
        // Update last size
        lastSizeRef.current.width = size.width
        lastSizeRef.current.height = size.height
      }
    }
  }, [gl, size.width, size.height])
  
  return null
}

export const WebGPUCanvas = ({ children, camera, onPreloadProgress, onSceneReady, mediaQueryKey }) => {
  const [rendererType, setRendererType] = useState(
    WebGPU.isAvailable() ? 'WebGPU' : 'WebGL 2.0'
  )
  const isWebGPUAvailable = WebGPU.isAvailable()

  return (
    <>
      <RendererInfo rendererType={rendererType} />
      <Canvas
        camera={camera}
        performance={{ min: 0.5, debounce: 300 }}
        shadows
        frameloop="always"
        resize={{ debounce: 100 }}
        gl={async (props) => {
          if (isWebGPUAvailable) {
            try {
              const renderer = new WebGPURenderer({
                ...props,
                forceWebGL: false,
              })
              await renderer.init()

              // Explicitly enable shadow maps — WebGPURenderer does not pick up
              // R3F's <Canvas shadows> prop automatically the way WebGLRenderer does.
              renderer.shadowMap.enabled = true
              renderer.shadowMap.type    = PCFSoftShadowMap
              
              
              // Set initial size from canvas element to prevent first-frame mismatch
              if (props.canvas) {
                const width = props.canvas.clientWidth
                const height = props.canvas.clientHeight
                renderer.setPixelRatio(getClampedDPR())
                renderer.setSize(width, height, false)
              }
              
              setRendererType('WebGPU')
              return renderer
            } catch (err) {
              console.warn('WebGPU initialization failed, falling back to WebGL:', err)
              setRendererType('WebGL 2.0 (fallback)')
              return new WebGLRenderer({
                ...props,
                antialias: true,
              })
            }
          } else {
            setRendererType('WebGL 2.0')
            return new WebGLRenderer({
              ...props,
              antialias: true,
            })
          }
        }}
      >
        <color attach="background" args={['#000000']} />
        <WebGPUResizeHandler />
        <FrameLoopController />
        <IdleDetector mediaQueryKey={mediaQueryKey} />
        <PreloadManager onProgress={onPreloadProgress}>
          <SceneReadyProvider onAllReady={onSceneReady}>
            {children}
          </SceneReadyProvider>
        </PreloadManager>
      </Canvas>
    </>
  )
}

export default WebGPUCanvas
